import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { useBookings, useBookingActions, useSlotAvailability, useSlotConfiguration } from '../../hooks/booking/useBookingContextSlices';

const normalizeTime = (t) => {
  if (!t || typeof t !== 'string') return '';
  return t.slice(0, 5);
};

// GA-005: on validation failure, bring the first invalid field into view and
// focus it. Fields are tagged with `data-field="<errorKey>"` in the form; we
// walk them in DOM order so the *first* one on the page (top-most) wins.
const focusFirstInvalidField = (errs) => {
  if (typeof document === 'undefined' || !errs) return;
  const keys = Object.keys(errs).filter((k) => errs[k] && k !== 'submit');
  if (!keys.length) return;
  const nodes = Array.from(document.querySelectorAll('[data-field]'));
  const target =
    nodes.find((n) => keys.includes(n.getAttribute('data-field'))) ||
    document.querySelector(`[data-field="${keys[0]}"]`);
  if (!target) return;
  try {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } catch (_) {
    /* older browsers: scrollIntoView without options */
    target.scrollIntoView();
  }
  target.focus?.();
};

export function useBookingFormV2() {
  const { groundId, serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { bookings, loading: contextLoading } = useBookings();
  const { addBooking } = useBookingActions();
  const { getGroundById, getServiceById, calculateSlotPrice, getSlotAvailability } = useSlotAvailability();
  const { slotConfiguration } = useSlotConfiguration();

  const ground = getGroundById(groundId);
  const service = getServiceById(groundId, serviceId);
  const preselectedData = location.state || null;
  const isMatch = service?.type === 'cricket_match';

  // ── Configured service data ──────────────────────────────────────────
  const configuredServiceData = useMemo(() => {
    if (!slotConfiguration?.services?.length) return null;
    const stateConfig = preselectedData?.serviceConfig;
    if (stateConfig?.service) return stateConfig;

    const routeId = parseInt(serviceId);
    const byId = slotConfiguration.services.find(
      (c) => parseInt(c?.service?.id) === routeId
    );
    if (byId) return byId;

    const byName = slotConfiguration.services.find(
      (c) =>
        (c?.service?.name || '').trim().toLowerCase() ===
        (service?.name || '').trim().toLowerCase()
    );
    if (byName) return byName;

    return slotConfiguration.services.find(
      (c) =>
        c?.service?.type === service?.type &&
        c?.service?.category === service?.category
    );
  }, [slotConfiguration, serviceId, service, preselectedData]);

  const effectiveServiceId = useMemo(() => {
    const cid = parseInt(configuredServiceData?.service?.id);
    if (!Number.isNaN(cid) && cid > 0) return cid;
    const rid = parseInt(serviceId);
    return Number.isNaN(rid) ? null : rid;
  }, [configuredServiceData, serviceId]);

  const dateRange = configuredServiceData ? slotConfiguration?.dateRange : null;

  const configuredSlots = useMemo(() => {
    if (configuredServiceData?.service?.slots?.length > 0)
      return configuredServiceData.service.slots;
    if (configuredServiceData?.slots?.length > 0)
      return configuredServiceData.slots;
    return service?.dailySlots || [];
  }, [configuredServiceData, service]);

  // ── Price resolution (mirrors V1 logic exactly) ──────────────────────
  const getSlotConfiguredPrice = useMemo(() => {
    return (slotId, bookingDate) => {
      if (
        !configuredServiceData?.priceSetup?.weeklyPrices ||
        !slotId ||
        !bookingDate
      )
        return null;

      const dateObj =
        bookingDate instanceof Date ? bookingDate : new Date(bookingDate);
      if (isNaN(dateObj.getTime())) return null;

      const dayName = format(dateObj, 'EEEE').toLowerCase();
      const dayPrices =
        configuredServiceData.priceSetup.weeklyPrices?.[dayName] || {};
      const slotIdStr = String(slotId);
      const cfgSlotList =
        configuredServiceData?.service?.slots ||
        configuredServiceData?.slots ||
        [];

      if (dayPrices[slotId] != null) return parseFloat(dayPrices[slotId]) || 0;
      if (dayPrices[slotIdStr] != null)
        return parseFloat(dayPrices[slotIdStr]) || 0;

      const matchedKey = Object.keys(dayPrices).find(
        (k) => String(k) === slotIdStr
      );
      if (matchedKey !== undefined)
        return parseFloat(dayPrices[matchedKey]) || 0;

      let selStart = null;
      let selEnd = null;
      const fromCfg = cfgSlotList.find((s) => String(s.id) === slotIdStr);
      if (fromCfg) {
        selStart = normalizeTime(fromCfg.startTime);
        selEnd = normalizeTime(fromCfg.endTime);
      } else {
        const fromSvc = (service?.dailySlots || []).find(
          (s) => String(s.id) === slotIdStr
        );
        if (fromSvc) {
          selStart = normalizeTime(fromSvc.startTime);
          selEnd = normalizeTime(fromSvc.endTime);
        } else if (preselectedData?.preselectedSlot) {
          selStart = normalizeTime(preselectedData.preselectedSlot.startTime);
          selEnd = normalizeTime(preselectedData.preselectedSlot.endTime);
        }
      }

      if (selStart && selEnd) {
        const mapped = cfgSlotList.find(
          (s) =>
            normalizeTime(s.startTime) === selStart &&
            normalizeTime(s.endTime) === selEnd
        );
        if (mapped) {
          if (dayPrices[mapped.id] != null)
            return parseFloat(dayPrices[mapped.id]) || 0;
          if (dayPrices[String(mapped.id)] != null)
            return parseFloat(dayPrices[String(mapped.id)]) || 0;
        }
      }

      const allDays = Object.values(
        configuredServiceData.priceSetup.weeklyPrices || {}
      );
      for (const prices of allDays) {
        if (!prices || typeof prices !== 'object') continue;
        if (prices[slotId] != null) {
          const v = parseFloat(prices[slotId]) || 0;
          if (v > 0) return v;
        }
        if (prices[slotIdStr] != null) {
          const v = parseFloat(prices[slotIdStr]) || 0;
          if (v > 0) return v;
        }
        if (selStart && selEnd) {
          const mapped = cfgSlotList.find(
            (s) =>
              normalizeTime(s.startTime) === selStart &&
              normalizeTime(s.endTime) === selEnd
          );
          if (mapped) {
            const mv =
              parseFloat(
                prices[mapped.id] ?? prices[String(mapped.id)]
              ) || 0;
            if (mv > 0) return mv;
          }
        }
      }

      for (const prices of allDays) {
        if (!prices || typeof prices !== 'object') continue;
        const first = Object.values(prices)
          .map((v) => parseFloat(v) || 0)
          .find((v) => v > 0);
        if (first) return first;
      }

      return null;
    };
  }, [configuredServiceData, preselectedData, service]);

  const serviceFallbackPrice = useMemo(() => {
    const weekly = configuredServiceData?.priceSetup?.weeklyPrices || {};
    for (const dp of Object.values(weekly)) {
      if (!dp || typeof dp !== 'object') continue;
      const nz = Object.values(dp)
        .map((v) => parseFloat(v) || 0)
        .find((v) => v > 0);
      if (nz) return nz;
    }
    const cfgDef = (configuredSlots || [])
      .map((s) => parseFloat(s?.defaultPrice || s?.price) || 0)
      .find((v) => v > 0);
    if (cfgDef) return cfgDef;
    const svcDef = (service?.dailySlots || [])
      .map((s) => parseFloat(s?.defaultPrice || s?.price) || 0)
      .find((v) => v > 0);
    if (svcDef) return svcDef;
    return 0;
  }, [configuredServiceData, configuredSlots, service]);

  // ── Form state ───────────────────────────────────────────────────────
  const [bookingData, setBookingData] = useState({
    date: preselectedData?.preselectedDate
      ? format(new Date(preselectedData.preselectedDate), 'yyyy-MM-dd')
      : format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    timeSlot: preselectedData?.preselectedSlot
      ? `${preselectedData.preselectedSlot.startTime}-${preselectedData.preselectedSlot.endTime}`
      : '',
    slotId: preselectedData?.preselectedSlot?.id || null,
    playerCount: 1,
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    specialRequests: '',
  });

  const [matchingSlot, setMatchingSlot] = useState(null);
  const [bookingType, setBookingType] = useState('both');
  const [selectedTeamSide, setSelectedTeamSide] = useState('teamA');
  const [teamDetails, setTeamDetails] = useState({
    teamA: { name: '', captain: '', players: '', mobile: '' },
    teamB: { name: '', captain: '', players: '', mobile: '' },
  });
  const [matchDetails, setMatchDetails] = useState({
    ballType: 'leather',
    ballColor: 'red',
    umpire: '2',
    oversCount: 20,
  });
  const [paymentDetails, setPaymentDetails] = useState({
    paymentMode: 'cash',
    discountCoupon: '',
    discountAmount: 0,
    discountType: 'amount',
    advanceAmount: 0,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);

  const existingBookings = preselectedData?.existingBookings || [];
  const isEditMode = preselectedData?.isEditMode || false;

  // ── Selected slot price ──────────────────────────────────────────────
  const getTimeRangeFromValue = (val) => {
    if (!val || typeof val !== 'string') return '';
    const m = val.match(/\b\d{2}:\d{2}-\d{2}:\d{2}\b/);
    return m ? m[0] : val;
  };

  const getSelectedSlotPrice = useMemo(() => {
    if (!bookingData.slotId) return 0;
    let basePrice = 0;
    const range = getTimeRangeFromValue(bookingData.timeSlot);

    const inCfg = configuredSlots?.find(
      (s) =>
        String(s.id) === String(bookingData.slotId) ||
        `${s.startTime}-${s.endTime}` === range
    );
    if (inCfg) {
      const cp = getSlotConfiguredPrice(bookingData.slotId, bookingData.date);
      basePrice = cp ?? (inCfg.defaultPrice || inCfg.price || 0);
    } else {
      const inSvc = service?.dailySlots?.find(
        (s) =>
          String(s.id) === String(bookingData.slotId) ||
          `${s.startTime}-${s.endTime}` === range
      );
      if (inSvc) {
        const cp = getSlotConfiguredPrice(inSvc.id, bookingData.date);
        basePrice = cp ?? (inSvc.defaultPrice || inSvc.price || 0);
      } else if (preselectedData?.preselectedPrice) {
        basePrice = preselectedData.preselectedPrice;
      } else {
        basePrice = calculateSlotPrice(
          parseInt(groundId),
          effectiveServiceId,
          bookingData.slotId
        );
      }
    }

    if (!basePrice || basePrice <= 0) {
      const nav =
        preselectedData?.preselectedPrice ??
        preselectedData?.preselectedSlot?.price;
      if (nav) basePrice = parseFloat(nav) || 0;
    }

    if ((!basePrice || basePrice <= 0) && isMatch) {
      basePrice = serviceFallbackPrice || 0;
    }

    if (isMatch && bookingType === 'single') return basePrice / 2;
    return basePrice;
  }, [
    bookingData.slotId,
    bookingData.date,
    bookingData.timeSlot,
    configuredSlots,
    service,
    preselectedData,
    groundId,
    effectiveServiceId,
    calculateSlotPrice,
    isMatch,
    bookingType,
    getSlotConfiguredPrice,
    serviceFallbackPrice,
  ]);

  // ── Half / fully booked detection ────────────────────────────────────
  const findSlotBookings = useCallback(
    (slotId, date) => {
      if (!slotId || !date) return [];
      let d = date;
      if (d instanceof Date) d = format(d, 'yyyy-MM-dd');
      else if (typeof d === 'string') {
        const parsed = new Date(d);
        if (!isNaN(parsed.getTime())) d = format(parsed, 'yyyy-MM-dd');
      }
      return (bookings || []).filter((b) => {
        if (b.status === 'cancelled') return false;
        let bd = '';
        if (b.date) {
          if (typeof b.date === 'string') {
            const p = new Date(b.date);
            bd = !isNaN(p.getTime()) ? format(p, 'yyyy-MM-dd') : b.date;
          } else bd = format(b.date, 'yyyy-MM-dd');
        }
        if (bd !== d) return false;
        if (parseInt(b.groundId) !== parseInt(groundId)) return false;
        if (parseInt(b.serviceId) !== parseInt(effectiveServiceId))
          return false;
        if (
          b.slotId === slotId ||
          String(b.slotId) === String(slotId) ||
          parseInt(b.slotId) === parseInt(slotId)
        )
          return true;
        if (b.timeSlot && bookingData.timeSlot)
          return b.timeSlot === bookingData.timeSlot;
        return false;
      });
    },
    [bookings, groundId, effectiveServiceId, bookingData.timeSlot]
  );

  const isSlotHalfBooked = useMemo(() => {
    if (!isMatch || !bookingData.slotId || !bookingData.date) return false;
    try {
      const sb = findSlotBookings(bookingData.slotId, bookingData.date);
      return sb.length === 1 && !sb.some((b) => b.bookingType === 'both');
    } catch {
      return false;
    }
  }, [isMatch, bookingData.slotId, bookingData.date, findSlotBookings]);

  const isSlotFullyBooked = useMemo(() => {
    if (!bookingData.slotId || !bookingData.date || !isMatch) return false;
    try {
      const sb = findSlotBookings(bookingData.slotId, bookingData.date);
      return sb.length >= 2 || sb.some((b) => b.bookingType === 'both');
    } catch {
      return false;
    }
  }, [bookingData.slotId, bookingData.date, isMatch, findSlotBookings]);

  const alreadyBookedSide = useMemo(() => {
    if (!isMatch || !isSlotHalfBooked || !bookingData.slotId || !bookingData.date) return null;
    try {
      const sb = findSlotBookings(bookingData.slotId, bookingData.date);
      if (sb.length !== 1) return null;
      const existing = sb[0];
      if (existing.bookedTeamSide === 'teamA' || existing.bookedTeamSide === 'teamB') {
        return existing.bookedTeamSide;
      }
      const eb = existingBookings || [];
      if (eb.length === 1) {
        const side = eb[0]?.bookedTeamSide || eb[0]?.booked_team_side;
        if (side === 'teamA' || side === 'teamB') return side;
      }
      return 'teamA';
    } catch {
      return null;
    }
  }, [isMatch, isSlotHalfBooked, bookingData.slotId, bookingData.date, findSlotBookings, existingBookings]);

  const availableTeamSide = alreadyBookedSide === 'teamA' ? 'teamB' : 'teamA';

  // ── Effects ──────────────────────────────────────────────────────────

  // Auto-switch to single when half-booked, selecting the available side
  useEffect(() => {
    if (isMatch && isSlotHalfBooked && bookingType === 'both') {
      setBookingType('single');
      setSelectedTeamSide(availableTeamSide);
    }
  }, [isSlotHalfBooked, isMatch, bookingType, availableTeamSide]);

  // Match preselected slot on mount
  useEffect(() => {
    if (!preselectedData?.preselectedSlot) return;
    const ps = preselectedData.preselectedSlot;
    const pStart = normalizeTime(ps.startTime);
    const pEnd = normalizeTime(ps.endTime);

    let found =
      configuredSlots?.find(
        (s) =>
          s.id === ps.id ||
          (normalizeTime(s.startTime) === pStart &&
            normalizeTime(s.endTime) === pEnd)
      ) ||
      service?.dailySlots?.find(
        (s) =>
          s.id === ps.id ||
          (s.startTime === ps.startTime && s.endTime === ps.endTime)
      ) ||
      service?.slots?.find(
        (s) =>
          s.id === ps.id ||
          (s.startTime === ps.startTime && s.endTime === ps.endTime)
      );

    if (!found && pStart && pEnd) {
      const mapped = configuredSlots?.find(
        (s) =>
          normalizeTime(s.startTime) === pStart &&
          normalizeTime(s.endTime) === pEnd
      );
      if (mapped) found = mapped;
    }

    if (found) {
      setMatchingSlot(found);
      setBookingData((p) => ({
        ...p,
        slotId: found.id,
        // GA-004: store the raw range only; the name is kept in slotName and
        // composed at render, so we never persist "name (range)" duplication.
        timeSlot: `${found.startTime}-${found.endTime}`,
        slotName: found.name || null,
      }));
    } else if (ps.startTime && ps.endTime) {
      setBookingData((p) => ({
        ...p,
        slotId: ps.id || null,
        timeSlot: `${ps.startTime}-${ps.endTime}`,
        slotName: ps.name || null,
      }));
    }
  }, [preselectedData, service, configuredSlots]);

  // Load edit-mode data
  useEffect(() => {
    if (!isEditMode || !existingBookings?.length) return;

    if (isMatch) {
      const hasBoth = existingBookings.some((b) => b.bookingType === 'both');
      if (existingBookings.length >= 2 || hasBoth) {
        setBookingType('both');
        existingBookings.forEach((b) => {
          if (b.teamDetails?.teamA)
            setTeamDetails((p) => ({
              ...p,
              teamA: {
                name: b.teamDetails.teamA.name || '',
                captain: b.teamDetails.teamA.captain || '',
                players: b.teamDetails.teamA.players || '',
                mobile: b.teamDetails.teamA.mobile || '',
              },
            }));
          if (b.teamDetails?.teamB)
            setTeamDetails((p) => ({
              ...p,
              teamB: {
                name: b.teamDetails.teamB.name || '',
                captain: b.teamDetails.teamB.captain || '',
                players: b.teamDetails.teamB.players || '',
                mobile: b.teamDetails.teamB.mobile || '',
              },
            }));
          if (
            b.bookedTeamSide &&
            (b.bookingType === 'single' || existingBookings.length === 1)
          ) {
            setBookingType('single');
            setSelectedTeamSide(b.bookedTeamSide);
          }
          if (b.matchDetails) setMatchDetails(b.matchDetails);
          if (!editingBookingId && b.id) setEditingBookingId(b.id);
        });
      } else if (existingBookings.length === 1) {
        const b = existingBookings[0];
        setBookingType('single');
        setSelectedTeamSide(b.bookedTeamSide || 'teamA');
        if (b.teamDetails) {
          const side = b.bookedTeamSide || 'teamA';
          const team = b.teamDetails[side] || {};
          setTeamDetails((p) => ({
            ...p,
            [side]: {
              name: team.name || '',
              captain: team.captain || '',
              players: team.players || '',
              mobile: team.mobile || '',
            },
          }));
        }
        if (b.matchDetails) setMatchDetails(b.matchDetails);
        if (b.id) setEditingBookingId(b.id);
      }
    }

    const first = existingBookings[0];
    if (first) {
      setBookingData((p) => ({
        ...p,
        contactName: first.contactName || first.bookedBy || '',
        contactPhone: first.contactPhone || first.bookedByPhone || '',
        contactEmail: first.contactEmail || '',
        specialRequests: first.specialRequests || '',
      }));
      if (first.paymentDetails) {
        setPaymentDetails((p) => ({
          ...p,
          paymentMode: first.paymentDetails.paymentMode || 'cash',
          discountAmount: first.discountAmount || 0,
          discountType: first.paymentDetails.discountType || 'amount',
          advanceAmount: first.advanceAmount || 0,
        }));
      }
    }
  }, [isEditMode, existingBookings, isMatch, editingBookingId]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleInputChange = useCallback((field, value) => {
    setBookingData((p) => ({ ...p, [field]: value }));
    setErrors((p) => ({ ...p, [field]: '' }));
  }, []);

  const handleTeamChange = useCallback((teamKey, field, value) => {
    setTeamDetails((p) => ({
      ...p,
      [teamKey]: { ...p[teamKey], [field]: value },
    }));
    setErrors((p) => ({ ...p, [`${teamKey}_${field}`]: '' }));
  }, []);

  // ── Validation ───────────────────────────────────────────────────────
  const validateForm = useCallback(() => {
    const e = {};
    if (!bookingData.timeSlot || !bookingData.slotId)
      e.timeSlot = 'Please select a time slot';

    if (isMatch) {
      const teams =
        bookingType === 'both' ? ['teamA', 'teamB'] : [selectedTeamSide];
      teams.forEach((tk) => {
        const t = teamDetails[tk];
        if (!t.name.trim()) e[`${tk}_name`] = 'Team name is required';
        if (!t.captain.trim())
          e[`${tk}_captain`] = 'Captain name is required';
        if (!t.mobile.trim()) {
          e[`${tk}_mobile`] = 'Mobile number is required';
        } else {
          const digits = t.mobile.replace(/\D/g, '');
          if (digits.length !== 10)
            e[`${tk}_mobile`] = 'Please enter a valid 10-digit mobile number';
        }
      });
    }

    setErrors(e);
    // GA-005: return the error map (not just a boolean) so callers can surface
    // a summary and scroll to the first invalid field.
    return e;
  }, [bookingData, isMatch, bookingType, selectedTeamSide, teamDetails]);

  // ── Compute amounts ──────────────────────────────────────────────────
  const computeAmounts = useCallback(() => {
    let baseAmount =
      typeof getSelectedSlotPrice === 'number' ? getSelectedSlotPrice : 0;
    if (!bookingData.slotId) {
      baseAmount =
        service?.dailySlots?.[0]?.defaultPrice ||
        service?.dailySlots?.[0]?.price ||
        service?.price ||
        0;
    }
    baseAmount = parseFloat(baseAmount) || 0;

    const discInput = parseFloat(paymentDetails.discountAmount) || 0;
    let discountAmount = 0;
    if (discInput > 0) {
      discountAmount =
        paymentDetails.discountType === 'percentage'
          ? (baseAmount * discInput) / 100
          : discInput;
    }

    const totalAmount = Math.max(0, baseAmount - discountAmount);
    const advanceAmount = parseFloat(paymentDetails.advanceAmount) || 0;
    const balanceAmount = Math.max(0, totalAmount - advanceAmount);

    return { baseAmount, discountAmount, totalAmount, advanceAmount, balanceAmount };
  }, [getSelectedSlotPrice, bookingData.slotId, service, paymentDetails]);

  // ── Submit (identical payload structure to V1) ───────────────────────
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        focusFirstInvalidField(validationErrors);
        return;
      }

      if (!effectiveServiceId) {
        setErrors({
          submit: 'Service mapping failed. Please go back and retry.',
        });
        return;
      }

      if (bookingData.slotId && bookingData.date) {
        const availability = getSlotAvailability(
          bookingData.date,
          parseInt(groundId),
          effectiveServiceId
        );
        const ss = availability[bookingData.slotId];
        if (ss?.status === 'booked') {
          if (
            isMatch &&
            ss.bookings?.length >= 2 &&
            (!isEditMode || !existingBookings?.length)
          ) {
            setErrors({ timeSlot: 'This slot is fully booked by both teams.' });
            return;
          } else if (!isMatch) {
            setErrors({ timeSlot: 'This slot is already fully booked.' });
            return;
          }
        }
        if (
          isMatch &&
          bookingType === 'both' &&
          (ss?.status === 'half-booked' || ss?.bookings?.length === 1)
        ) {
          setErrors({
            timeSlot: 'Slot is half-booked. Please select Single Team Booking.',
          });
          return;
        }
      }

      let derivedName = bookingData.contactName || '';
      let derivedPhone = bookingData.contactPhone || '';
      if (isMatch) {
        const pk = bookingType === 'single' ? selectedTeamSide : 'teamA';
        const pt = teamDetails[pk] || {};
        if (!derivedName) derivedName = pt.captain || pt.name || '';
        if (!derivedPhone) derivedPhone = pt.mobile || '';
      }

      setIsSubmitting(true);
      setIsLoading(true);

      try {
        const {
          baseAmount,
          discountAmount,
          totalAmount,
          advanceAmount,
          balanceAmount,
        } = computeAmounts();

        const allSlots = configuredSlots || service?.dailySlots || [];
        const selectedSlot = allSlots.find(
          (s) => s.id === bookingData.slotId
        );
        const slotName = selectedSlot?.name || bookingData.slotName || null;

        const common = {
          groundId: parseInt(groundId),
          serviceId: effectiveServiceId,
          slotId: bookingData.slotId || service?.dailySlots?.[0]?.id,
          date: bookingData.date,
          timeSlot: bookingData.timeSlot,
          slotName,
          serviceType: service?.type,
          serviceName: service?.name,
          groundName: ground?.name,
          ...bookingData,
          bookedBy: derivedName,
          bookedByPhone: derivedPhone,
          customPrice: null,
          paymentDetails: {
            ...paymentDetails,
            baseAmount,
            discountAmount,
            discountType: paymentDetails.discountType,
            totalAmount,
            advanceAmount,
          },
          baseAmount,
          discountAmount,
          totalAmount,
          advanceAmount,
          balanceAmount,
          bookingDate: new Date().toISOString(),
        };

        let confirmed;
        if (isMatch && bookingType === 'both') {
          confirmed = await addBooking({
            ...common,
            bookingType: 'both',
            bookedTeamSide: null,
            teamDetails: {
              teamA: teamDetails.teamA,
              teamB: teamDetails.teamB,
            },
            matchDetails,
            guestList: null,
          });
        } else {
          const finalTeam =
            isMatch && bookingType === 'single'
              ? { [selectedTeamSide]: teamDetails[selectedTeamSide] }
              : null;
          confirmed = await addBooking({
            ...common,
            bookingType: isMatch ? bookingType : null,
            bookedTeamSide:
              isMatch && bookingType === 'single' ? selectedTeamSide : null,
            teamDetails: finalTeam,
            matchDetails: isMatch ? matchDetails : null,
            guestList: null,
          });
        }

        setShowSuccess(true);
        setConfirmedBooking(confirmed);
      } catch (error) {
        setErrors({
          submit:
            error?.message || 'Failed to confirm booking. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
        setIsLoading(false);
      }
    },
    [
      validateForm,
      effectiveServiceId,
      bookingData,
      isMatch,
      bookingType,
      selectedTeamSide,
      teamDetails,
      matchDetails,
      paymentDetails,
      computeAmounts,
      configuredSlots,
      service,
      ground,
      groundId,
      addBooking,
      getSlotAvailability,
      isEditMode,
      existingBookings,
    ]
  );

  const handleNotificationClose = useCallback(() => {
    setShowSuccess(false);
    setConfirmedBooking(null);
    navigate('/dashboard/my-bookings');
  }, [navigate]);

  return {
    ground,
    service,
    groundId,
    serviceId,
    isMatch,
    isEditMode,
    configuredSlots,
    dateRange,
    preselectedData,
    bookingData,
    setBookingData,
    bookingType,
    setBookingType,
    selectedTeamSide,
    setSelectedTeamSide,
    teamDetails,
    setTeamDetails,
    matchDetails,
    setMatchDetails,
    paymentDetails,
    setPaymentDetails,
    errors,
    setErrors,
    isSubmitting,
    isLoading,
    showSuccess,
    confirmedBooking,
    getSelectedSlotPrice,
    isSlotHalfBooked,
    isSlotFullyBooked,
    alreadyBookedSide,
    availableTeamSide,
    getSlotConfiguredPrice,
    effectiveServiceId,
    handleInputChange,
    handleTeamChange,
    validateForm,
    handleSubmit,
    handleNotificationClose,
    handleNotificationConfirm: handleNotificationClose,
    computeAmounts,
    contextLoading,
  };
}
